import React, { useEffect, useRef, useState, useCallback } from "react";
import DropdownButton from "app/components/DropdownButton";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import CheckBox from "app/components/inputs/CheckBox";
import { cls } from "app/utils";
import { useHistory, useLocation } from "react-router-dom";
import ResetFiltersButton from "./ResetFiltersButton";
import SearchBar from "app/components/inputs/SearchBar";

type StatusFilter = {
  active: boolean;
  inactive: boolean;
};

type CollectionMethodFilter = {
  online: boolean;
  offline: boolean;
};

const DEFAULT_STATUS: StatusFilter = {
  active: true,
  inactive: true,
};

const DEFAULT_COLLECTION_METHOD: CollectionMethodFilter = {
  online: true,
  offline: true,
};

const OfferingsFilters = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [nameFilter, setNameFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(DEFAULT_STATUS);
  const [collectionMethodFilter, setCollectionMethodFilter] = useState<CollectionMethodFilter>(DEFAULT_COLLECTION_METHOD);
  const [activeDropdown, setActiveDropdown] = useState<null | 'status' | 'collection'>(null);

  const dropdownRefs = {
    status: useRef<HTMLDivElement>(null),
    collection: useRef<HTMLDivElement>(null),
  };

  // Debounce timer for name filter
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const title = params.get('title');
    if (title) {
      setNameFilter(title);
    }

    const status = params.get('status');
    if (status) {
      const statusValues = status.split(',');
      setStatusFilter({
        active: statusValues.includes('active'),
        inactive: statusValues.includes('inactive'),
      });
    }

    const collect_type = params.get('collect_type');
    if (collect_type) {
      const methodValues = collect_type.split(',');
      setCollectionMethodFilter({
        online: methodValues.includes('online'),
        offline: methodValues.includes('offline'),
      });
    }
  }, []);

  // Update URL and trigger filter change
  const updateFilters = useCallback((
    name: string,
    status: StatusFilter,
    collectMethod: CollectionMethodFilter
  ) => {
    const params = new URLSearchParams();

    if (name) {
      params.set('title', name);
    }

    const statusValues = [];
    if (status.active) {
      statusValues.push('active');
    }
    if (status.inactive) {
      statusValues.push('inactive');
    }
    if (statusValues.length > 0 && statusValues.length < 2) {
      params.set('status', statusValues.join(','));
    }

    const methodValues = [];
    if (collectMethod.online) {
      methodValues.push('online');
    }
    if (collectMethod.offline) {
      methodValues.push('offline');
    }
    if (methodValues.length > 0 && methodValues.length < 2) {
      params.set('collect_type', methodValues.join(','));
    }

    const searchString = params.toString();
    const newPath = searchString ? `${location.pathname}?${searchString}` : location.pathname;

    history.replace(newPath);
  }, [history, location.pathname]);

  // Handle name filter change with debounce
  const handleNameChange = (value: string) => {
    setNameFilter(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      updateFilters(value, statusFilter, collectionMethodFilter);
    }, 300);
  };

  // Handle status filter
  const selectStatus = (key: keyof StatusFilter) => {
    const newStatusFilter = {
      ...statusFilter,
      [key]: !statusFilter[key],
    };
    setStatusFilter(newStatusFilter);
    updateFilters(nameFilter, newStatusFilter, collectionMethodFilter);
  };

  // Handle collection method filter
  const selectCollectionMethod = (key: keyof CollectionMethodFilter) => {
    const newCollectionMethodFilter = {
      ...collectionMethodFilter,
      [key]: !collectionMethodFilter[key],
    };
    setCollectionMethodFilter(newCollectionMethodFilter);
    updateFilters(nameFilter, statusFilter, newCollectionMethodFilter);
  };

  // Reset all filters
  const resetFilters = () => {
    setNameFilter("");
    setStatusFilter(DEFAULT_STATUS);
    setCollectionMethodFilter(DEFAULT_COLLECTION_METHOD);

    history.replace(location.pathname);
  };

  // Get selected count for display
  const getSelectedStatusCount = () => {
    return Object.values(statusFilter).filter(Boolean).length;
  };

  const getSelectedCollectionMethodCount = () => {
    return Object.values(collectionMethodFilter).filter(Boolean).length;
  };

  const getStatusFilterText = () => {
    const count = getSelectedStatusCount();

    if (count === 2 || count === 0) {
      return t('All Statuses');
    }

    // Only one is selected, show which one
    if (statusFilter.active) {
      return t('Active');
    }
    return t('Inactive');
  };

  const getCollectionMethodFilterText = () => {
    const count = getSelectedCollectionMethodCount();

    if (count === 2 || count === 0) {
      return t('All Collection Methods');
    }

    // Only one is selected, show which one
    if (collectionMethodFilter.online) {
      return t('Online');
    }
    return t('Offline');
  };

  // Check if reset button should be displayed
  const displayResetButton = () => {
    return (
      nameFilter !== "" ||
      getSelectedStatusCount() !== 2 ||
      getSelectedCollectionMethodCount() !== 2
    );
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!activeDropdown) {
        return;
      }

      const target = event.target as Node;
      const currentRef = dropdownRefs[activeDropdown];

      if (currentRef.current && !currentRef.current.contains(target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  const StatusOptions = {
    active: t('Active'),
    inactive: t('Inactive'),
  };

  const CollectionMethodOptions = {
    online: t('Online'),
    offline: t('Offline'),
  };

  return (
    <div className={styles.filters_wrapper}>
      <div className={styles.filters_row}>
        <div className={styles.container}>
          {/* Name Filter */}
          <SearchBar
            className={styles.search_bar}
            placeholder={t("Search by name...")}
            value={nameFilter}
            onChange={(value) => handleNameChange(value)}
          />

          {/* Status Filter */}
          <div ref={dropdownRefs.status}>
            <DropdownButton
              hideButtonStyles
              btnContent={(
                <div className={cls({ [styles.inactive_filter]: getSelectedStatusCount() === 2 }, styles.selected_value)}>
                  <p>
                    {getStatusFilterText()}
                  </p>
                  <i className={cls("ico ico-chevron", { [styles.rotated]: activeDropdown === 'status' })} />
                </div>
              )}
              dropdownWrapperClass={styles.wrapper}
              dropdownClassname={styles.dropdown}
              onOpen={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
            >
              <div className={styles.actions_dropdown}>
                {Object.entries(StatusOptions).map(([key, value]) => (
                  <div
                    key={key}
                    className={styles.actions_list_item}
                    onClick={() => selectStatus(key as keyof StatusFilter)}
                  >
                    <p>{value}</p>
                    <CheckBox
                      key={key}
                      name={value}
                      className={styles.checkbox_input}
                      checked={statusFilter[key as keyof StatusFilter]}
                    />
                  </div>
                ))}
              </div>
            </DropdownButton>
          </div>

          {/* Collection Method Filter */}
          <div ref={dropdownRefs.collection}>
            <DropdownButton
              hideButtonStyles
              btnContent={(
                <div className={cls({ [styles.inactive_filter]: getSelectedCollectionMethodCount() === 2 }, styles.selected_value)}>
                  <p>
                    {getCollectionMethodFilterText()}
                  </p>
                  <i className={cls("ico ico-chevron", { [styles.rotated]: activeDropdown === 'collection' })} />
                </div>
              )}
              dropdownWrapperClass={styles.wrapper}
              dropdownClassname={styles.dropdown}
              onOpen={() => setActiveDropdown(activeDropdown === 'collection' ? null : 'collection')}
            >
              <div className={styles.actions_dropdown}>
                {Object.entries(CollectionMethodOptions).map(([key, value]) => (
                  <div
                    key={key}
                    className={styles.actions_list_item}
                    onClick={() => selectCollectionMethod(key as keyof CollectionMethodFilter)}
                  >
                    <p>{value}</p>
                    <CheckBox
                      key={key}
                      name={value}
                      className={styles.checkbox_input}
                      checked={collectionMethodFilter[key as keyof CollectionMethodFilter]}
                    />
                  </div>
                ))}
              </div>
            </DropdownButton>
          </div>
        </div>
        {displayResetButton() && <ResetFiltersButton onClick={resetFilters} />}
      </div>
    </div>
  );
};

export default OfferingsFilters;
